import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Instagram, Facebook, Linkedin, ShieldCheck, CheckCircle2, ArrowRight } from "lucide-react";
import api from "../lib/api";
import { Logo } from "../components/Logo";
import { STATS, fmt, ReactionsRow, StatCard, EngagementHero } from "../components/PostAnalytics";

const PLAT = { Instagram, Facebook, LinkedIn: Linkedin };

export default function SharedPost() {
  const { id } = useParams();
  const [post, setPost] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    api.get(`/public/posts/${id}`).then((r) => setPost(r.data)).catch(() => setErr(true));
  }, [id]);

  if (err) return (
    <div className="min-h-screen grid place-items-center" style={{ background: "#F7F8F6" }}>
      <div className="text-center"><Logo height={22} /><p className="text-ink-2 mt-4">This shared report is no longer available.</p></div>
    </div>
  );
  if (!post) return <div className="min-h-screen grid place-items-center text-ink-2" style={{ background: "#F7F8F6" }}>Loading report…</div>;

  const m = post.metrics || {};
  const b = post.branding;
  const PlatIcon = PLAT[post.platform] || Instagram;
  const cards = STATS.filter((s) => m[s.k] !== null && m[s.k] !== undefined);
  const trend = (post.reach_trend || []).map((v, i) => ({ d: i, v }));

  return (
    <div className="min-h-screen py-8 px-4" style={{ background: "#F7F8F6" }}>
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-5" data-testid="shared-branding">
          <div className="flex items-center gap-3">
            {b?.logo
              ? <img src={b.logo} alt={b?.agency} className="h-10 max-w-[160px] object-contain" data-testid="shared-logo" />
              : b
                ? <span className="grid place-items-center w-10 h-10 rounded-xl text-white font-bold shrink-0" style={{ background: b.color, fontSize: 15 }}>{b.initials}</span>
                : <Logo height={20} />}
            {b && (
              <div>
                <div className="font-bold text-ink leading-tight">{b.agency}</div>
                <div className="text-xs text-ink-3">{b.branch}</div>
              </div>
            )}
          </div>
          <span className="text-xs font-semibold rounded-full px-3 py-1" style={{ background: "var(--grad-green-soft)", color: "#5F8213" }}>Performance report</span>
        </div>

        <div className="card overflow-hidden" data-testid="shared-post">
          <div className="relative">
            <img src={post.image} alt="" className="w-full h-44 object-cover" />
            <div className="absolute inset-0" style={{ background: "linear-gradient(180deg, rgba(21,21,21,.1), rgba(21,21,21,.7))" }} />
            <div className="absolute left-6 bottom-4 right-6 text-white">
              <div className="flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider"><PlatIcon size={14} strokeWidth={1.75} /> {post.platform} · {post.format} <span className="opacity-70">· {post.date}</span></div>
              <h1 className="text-2xl font-bold tracking-tight mt-1">{post.title}</h1>
            </div>
          </div>

          <div className="p-5 space-y-3.5">
            <div className="grid grid-cols-2 gap-3">
              {[["Shared with client", post.shared_with_client], ["Opened by client", post.opened_by_client]].map(([l, val]) => (
                <div key={l} className="rounded-xl border p-3.5 flex items-center gap-3" style={{ borderColor: "#E7E8E4" }}>
                  <CheckCircle2 size={20} strokeWidth={2} color={val ? "#678A16" : "#9A9C90"} />
                  <div><div className="label-caps">{l}</div><div className="text-lg font-bold text-ink">{val ? "Yes" : "Not yet"}</div></div>
                </div>
              ))}
            </div>

            <EngagementHero m={m} trend={trend} />

            {m.reactions && (
              <div className="rounded-xl border p-4" style={{ borderColor: "#E7E8E4" }}>
                <div className="label-caps mb-2.5">Reactions</div>
                <ReactionsRow reactions={m.reactions} />
              </div>
            )}

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {cards.map((s) => <StatCard key={s.k} s={s} m={m} />)}
            </div>

            <div className="flex items-center gap-2 text-xs text-ink-2 pt-1">
              <ShieldCheck size={15} strokeWidth={1.75} color="#678A16" /> Verified connected-account analytics · shared read-only via Growth
            </div>
          </div>
        </div>

        <a href="/" className="mt-5 flex items-center justify-center gap-1.5 text-sm font-semibold text-ink-2 hover:text-ink transition-colors">
          Powered by Growth <ArrowRight size={14} strokeWidth={2} />
        </a>
      </div>
    </div>
  );
}
